import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, ArrowUpRight, Languages } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const LOGO_SRC = "/assets/logo.png";

const navConfig = [
  { to: "/", key: "nav.home", testId: "nav-home-link", end: true },
  { to: "/about", key: "nav.about", testId: "nav-about-link" },
  { to: "/appointment", key: "nav.appointment", testId: "nav-appointment-link" },
  { to: "/contact", key: "nav.contact", testId: "nav-contact-link" },
];

export const Navbar = () => {
  const { lang, setLang, t } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navItems = navConfig.map((n) => ({ ...n, label: t(n.key) }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled ? "bfl-glass" : "bg-white/95 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3 group">
          <img
            src={LOGO_SRC}
            alt="Bright FL Inspections logo"
            className="w-11 h-11 object-contain"
          />
          <div className="leading-tight">
            <div className="font-display font-black text-[#1F3460] text-lg tracking-tight">
              BRIGHT FL
            </div>
            <div className="text-[10px] tracking-[0.25em] font-bold text-[#5E6B85] -mt-0.5">
              INSPECTIONS
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              end={item.to === "/"}
              className={({ isActive }) =>
                `relative px-4 py-2 text-[13px] font-bold tracking-wider uppercase transition-colors ${
                  isActive ? "text-[#1F3460]" : "text-[#5E6B85] hover:text-[#1F3460]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span className="absolute -bottom-0.5 left-3 right-3 h-[3px] bg-[#6FA8DC]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            type="button"
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "en" ? "es" : "en")}
            className="flex items-center gap-1.5 px-3 py-2 border-2 border-[#1F3460] text-[#1F3460] hover:bg-[#1F3460] hover:text-white transition-all text-xs font-bold tracking-wider uppercase"
            aria-label="Toggle language"
          >
            <Languages className="w-3.5 h-3.5" />
            {lang === "en" ? "ES" : "EN"}
          </button>
          <Link to="/appointment" data-testid="nav-cta-appointment" className="btn-accent">
            {t("nav.cta_appointment")} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#1F3460]"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-overlay"
          className="lg:hidden absolute inset-x-0 top-[72px] bg-white border-t border-[#E2E8F0] shadow-xl"
        >
          <div className="px-5 py-6 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-${item.testId}`}
                className={({ isActive }) =>
                  `px-3 py-3 font-bold tracking-wider text-sm uppercase border-l-4 ${
                    isActive
                      ? "border-[#6FA8DC] text-[#1F3460] bg-slate-50"
                      : "border-transparent text-[#5E6B85]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/appointment"
              data-testid="mobile-nav-cta-appointment"
              className="btn-accent mt-3 justify-center"
            >
              {t("nav.cta_appointment")} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              type="button"
              data-testid="mobile-lang-toggle"
              onClick={() => setLang(lang === "en" ? "es" : "en")}
              className="mt-2 flex items-center justify-center gap-2 px-3 py-3 border-2 border-[#1F3460] text-[#1F3460] text-xs font-bold tracking-wider uppercase"
            >
              <Languages className="w-3.5 h-3.5" />
              {lang === "en" ? "Español" : "English"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  const { t } = useT();
  const navItems = navConfig.map((n) => ({ ...n, label: t(n.key) }));
  return (
    <footer data-testid="site-footer" className="bg-[#1F3460] text-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <img
                src={LOGO_SRC}
                alt="Bright FL Inspections"
                className="w-14 h-14 object-contain bg-white p-1"
              />
              <div className="overline" style={{ color: "#6FA8DC" }}>
                Bright FL Inspections
              </div>
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-black leading-tight">
              {t("footer.tagline")}
            </h3>
            <p className="mt-4 text-slate-300 max-w-md">
              {t("footer.subtitle")}
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="overline" style={{ color: "#6FA8DC" }}>
              {t("footer.navigate")}
            </div>
            <ul className="mt-4 space-y-2">
              {navItems.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`footer-${item.testId}`}
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline" style={{ color: "#6FA8DC" }}>
              {t("footer.contact")}
            </div>
            <ul className="mt-4 space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-1 text-[#6FA8DC]" />
                <a
                  href="mailto:brightflinspections@gmail.com"
                  data-testid="footer-email-link"
                  className="hover:text-white"
                >
                  brightflinspections@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-1 text-[#6FA8DC]" />
                <a href="tel:+12396927666" data-testid="footer-phone" className="hover:text-white">
                  (239) 692-7666
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-[#6FA8DC]" />
                <span>{t("footer.area")}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs text-slate-400">
          <div>© {new Date().getFullYear()} Bright FL Inspections. {t("footer.rights")}</div>
          <div className="tracking-[0.25em] uppercase">{t("footer.creds")}</div>
        </div>
      </div>
    </footer>
  );
};

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 pt-[72px]">{children}</main>
      <Footer />
    </div>
  );
}
